import * as m from '@staiter/i18n';
import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { SkeletonTable } from '~/components/data';
import { canOpenAdminPath } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import styles from './_auth.admin.module.css';

// S8 8.1 Admin shell — sub-nav + RBAC route guard (admin only).
// Hotfix 2026-05-23 (FE-140): wait for auth hydration before role-check —
// прямой URL `/admin/...` срабатывал ДО hydrate из localStorage, role=undefined
// → guard пропускал, дальше API возвращал 401 → interceptor kick'ал на /login.
export const Route = createFileRoute('/_auth/admin')({
  // FE-255 Pass 6: убран beforeLoad redirect — direct URL nav (`/admin/*`) срабатывал
  // ДО hydrate /me из localStorage → user=null → redirect kick'ал на /login или /.
  // RBAC-guard теперь в самом компоненте: ждём hydration + показываем skeleton,
  // если role≠admin — показываем «Нет доступа» вместо silent redirect.
  component: AdminShell,
});

// SET-AUDIT-1 (pass3 follow-up): добавлены ранее «скрытые» admin routes (bank-import,
// fx-rates, jobs, warehouse-access) — они существовали как страницы но не имели tab entries.
// CC-6 — подписи вкладок через i18n: раздел целиком оставался русским в en.
const TABS: Array<{ to: string; label: () => string }> = [
  { to: '/admin/integrations', label: m.admin_nav_integrations },
  { to: '/admin/sync-staging', label: m.admin_nav_sync_staging },
  { to: '/admin/approval-matrix', label: m.admin_nav_approval_matrix },
  { to: '/admin/holdings', label: m.admin_nav_holdings },
  { to: '/admin/entities', label: m.admin_nav_entities },
  { to: '/admin/users', label: m.admin_nav_users },
  { to: '/admin/warehouse-access', label: m.admin_nav_warehouse_access },
  { to: '/admin/bank-import', label: m.admin_nav_bank_import },
  { to: '/admin/fx-rates', label: m.admin_nav_fx_rates },
  { to: '/admin/jobs', label: m.admin_nav_jobs },
  { to: '/admin/email-templates', label: m.admin_nav_email_templates },
  { to: '/admin/audit', label: m.admin_nav_audit },
  { to: '/admin/memory', label: m.admin_nav_memory },
];

function AdminShell(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const user = useAuthStore((s) => s.user);

  // FE-255: skeleton пока user не hydrated. Только когда user resolved — проверяем role.
  if (!user) {
    return <SkeletonTable rows={6} cols={4} />;
  }
  // F21 (B33), С-I8 Р.5: `company_admin` пропускается в реестр и на экран «Доступы» сотрудника — без
  // вкладок админки, которые ему недоступны.
  if (!canOpenAdminPath(user.role, pathname)) {
    return (
      <div className={styles.shell}>
        <div className={styles.body}>
          <p style={{ color: 'var(--text-muted)', padding: '24px 0' }}>
            {m.ui_dostup_k_adminke_trebuet_roli_admin_svyazhites_s_administ()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      {user.role === 'admin' && (
        <nav className={styles.subnav} aria-label={m.admin_subnav_label()}>
          {TABS.map((t) => {
            const active = pathname === t.to || pathname.startsWith(`${t.to}/`);
            return (
              <Link
                key={t.to}
                to={t.to}
                className={[styles.tab, active ? styles.tabActive : ''].filter(Boolean).join(' ')}
              >
                {t.label()}
              </Link>
            );
          })}
        </nav>
      )}
      <div className={styles.body}>
        <Outlet />
      </div>
    </div>
  );
}
